// Third-party
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router';
import { LocationDescriptorObject } from 'history';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { getSavedBenchmarks, toggleSavedBenchmark } from '@/redux/modules/comparison-benchmarks/saved-benchmarks';
import { ADD_FUNDS_TO_COMPARISON, addBenchmarksToComparison } from '@/redux/modules/comparison';
import { addBenchmarksToPortfolio, loadNewBenchmark } from '@/redux/modules/edit-benchmark';
import { ADD_FUNDS_TO_PORTFOLIO } from '@/redux/modules/edit-portfolio';

// Columns Definition
import { getColumnsDefinition, SearchResultColumns } from '@/views/components/search/columns-definition/SearchResult';

// Components
import AdvancedSearchResult from '@/views/components/search/AdvancedSearchResult';

// Enums
import { SearchType } from '@/enums/AdvancedSearch';

// Selectors
import { selectIsLoadingEvery } from '@/redux/modules/ui/loaders';

// Types
import ComparisonBenchmark from '@/types/comparison-benchmark';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { FormattedMessage } from '@/utils/i18n';
import { searchObjectsOnFields } from '@/utils/search';
import { NAV_PATHS } from '@/utils/constants';
import { formatBenchmark } from '@/utils/advanced-search';

// Legacy Support
import withLegacyVersion from '@/views/components/hocs/withLegacyVersion';
import LegacySavedBenchmarks from './LegacySavedBenchmarks';

type DispatchProps = {
  getSavedBenchmarks: () => Promise<ComparisonBenchmark[]>;
  toggleSavedBenchmark: (benchmark: ComparisonBenchmark) => Promise<ComparisonBenchmark[]>;
  addBenchmarksToComparison: (benchmarks: ComparisonBenchmark[]) => Promise<void>;
  addBenchmarksToPortfolio: (benchmarks: ComparisonBenchmark[]) => Promise<void[]>;
  loadNewBenchmark: () => void;
};

type StateProps = {
  benchmarks: ComparisonBenchmark[];
  loading: boolean;
  addingToPortfolio: boolean;
  addingToComparison: boolean;
};

type Props = RouteComponentProps &
  DispatchProps &
  StateProps & {
    query?: string;
  };

interface State {
  selected: ComparisonBenchmark[];
  redirect?: string | LocationDescriptorObject;
}

class SavedBenchmarks extends React.Component<Props, State> {
  readonly state: State = {
    selected: [],
  };

  readonly tableColumns = [
    SearchResultColumns.NAME,
    SearchResultColumns.CLASSIFICATION,
    SearchResultColumns.THREE_YEARS,
    SearchResultColumns.FIVE_YEARS,
    SearchResultColumns.TEN_YEARS,
    SearchResultColumns.YT,
    SearchResultColumns.STD_DEV,
    SearchResultColumns.REMOVE_FROM_FAVORITES,
  ];

  onAddToPortfolio = () => {
    this.props.loadNewBenchmark();
    this.props.addBenchmarksToPortfolio(this.state.selected).then(() => {
      this.setState({ redirect: '/models/custom-benchmarks?newBenchmarkPortfolio=true' });
    });
  };

  onAddToComparison = () => {
    const { location } = this.props;
    this.props.addBenchmarksToComparison(this.state.selected).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({
        redirect: {
          pathname: NAV_PATHS.COMPARISON,
          state: { from: location },
        },
      });
    });
  };

  getActions = () => {
    const { addingToPortfolio, addingToComparison } = this.props;
    return [
      {
        loading: addingToPortfolio,
        icon: 'plus',
        label: <FormattedMessage id={`Search.Benchmarks.${addingToPortfolio ? 'CreatingCustom' : 'CreateCustom'}`} />,
        onClick: this.onAddToPortfolio,
      },
      {
        isLoading: addingToComparison,
        icon: 'plus',
        label: <FormattedMessage id={`Search.Benchmarks.${addingToComparison ? 'AddingToComparison' : 'AddToComparison'}`} />,
        onClick: this.onAddToComparison,
      },
    ];
  };

  getFilteredBenchmarks = () => {
    const { query, benchmarks } = this.props;

    const data = this.formatData(benchmarks);

    if (!query) {
      return data;
    }

    return searchObjectsOnFields(query, data, ['name', 'ticker', 'classification']);
  };

  toggleSaved = (row: ComparisonBenchmark) => {
    const { benchmarks } = this.props;
    const benchmark = benchmarks.find(f => f._id === row._id);
    if (!benchmark) {
      return;
    }

    this.props.toggleSavedBenchmark(benchmark);

    this.setState(prevState => ({
      selected: prevState.selected.filter(f => f !== row),
    }));
  };

  formatData = (benchmarks: ComparisonBenchmark[]) => {
    if (!benchmarks) {
      return [];
    }
    return benchmarks.map(benchmark => formatBenchmark(benchmark));
  };

  redirectToSearch = () => this.setState({ redirect: '/search/benchmarks' });

  handleRowSelectionChange = (selected: ComparisonBenchmark[]) => {
    this.setState({ selected });
  };

  render() {
    const { loading, query } = this.props;
    const { selected, redirect } = this.state;

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    const { columns } = getColumnsDefinition({
      onToggleSaved: this.toggleSaved,
      allowedColumns: this.tableColumns,
      type: SearchType.BENCHMARKS,
    });

    return (
      <AdvancedSearchResult
        actions={this.getActions()}
        canSelect
        className="saved-benchmarks"
        columns={columns}
        data={this.getFilteredBenchmarks()}
        itemType={SearchType.BENCHMARKS}
        loading={loading}
        searchQuery={query}
        selectedFunds={this.formatData(selected)}
        selectedTitle="Search.Funds.SelectedTitle"
        onRowSelectionChange={this.handleRowSelectionChange}
        showNoDataComponent
      />
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  benchmarks: state.comparisonBenchmarks.savedBenchmarks.benchmarks,
  loading: state.comparisonBenchmarks.savedBenchmarks.loading,
  addingToPortfolio: selectIsLoadingEvery(state, [ADD_FUNDS_TO_PORTFOLIO.type]),
  addingToComparison: selectIsLoadingEvery(state, [ADD_FUNDS_TO_COMPARISON.type]),
});

export default withLegacyVersion(
  connect(mapStateToProps, {
    getSavedBenchmarks,
    toggleSavedBenchmark,
    addBenchmarksToComparison,
    addBenchmarksToPortfolio,
    loadNewBenchmark,
  })(SavedBenchmarks),
  LegacySavedBenchmarks,
);
